import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const SEV_COLORS = [
  { key: "critical_count", label: "Kritické", color: "#dc2626" },
  { key: "high_count", label: "Závažné", color: "#ea580c" },
  { key: "medium_count", label: "Středně závažné", color: "#d97706" },
  { key: "unverifiable_count", label: "Neověřitelné", color: "#94a3b8" },
  { key: "ok_count", label: "V pořádku", color: "#059669" },
];

export const SeverityDonut = ({ summary, unverifiableLabel }) => {
  const labelOf = (s) => (s.key === "unverifiable_count" && unverifiableLabel ? unverifiableLabel : s.label);
  const data = SEV_COLORS.map((s) => ({ name: labelOf(s), value: summary?.[s.key] ?? 0, color: s.color }))
    .filter((d) => d.value > 0);
  const total = summary?.total_findings ?? data.reduce((a, b) => a + b.value, 0);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="severity-donut-chart">
      <h3 className="text-sm font-bold text-slate-900">Přehled kontrol</h3>
      <p className="text-xs text-slate-500">Rozložení výsledků podle závažnosti</p>
      <div className="relative mt-2 h-52">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" innerRadius={62} outerRadius={90} paddingAngle={2} strokeWidth={0}>
              {data.map((d, i) => <Cell key={i} fill={d.color} />)}
            </Pie>
            <Tooltip formatter={(v, n) => [`${v} kontrol`, n]} />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-extrabold text-slate-900">{total}</span>
          <span className="text-[11px] uppercase tracking-wider text-slate-400">kontrol</span>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
        {SEV_COLORS.map((s) => (
          <span key={s.key} className="flex items-center gap-1.5 text-xs text-slate-600">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: s.color }} />
            {labelOf(s)}: <b>{summary?.[s.key] ?? 0}</b>
          </span>
        ))}
      </div>
    </div>
  );
};

export const CoverageAreaBars = ({ areas, unverifiableLabel }) => {
  if (!areas?.length) return null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="coverage-area-bars">
      <h3 className="text-sm font-bold text-slate-900">Pokrytí vzdělávacích oblastí a kompetencí</h3>
      <p className="text-xs text-slate-500">Kolik závazných prvků je ve vašem ŠVP doloženo</p>
      <div className="mt-4 space-y-3">
        {areas.map((a) => {
          const pct = a.total ? Math.round(((a.covered + a.partial * 0.5) / a.total) * 100) : 0;
          return (
            <div key={a.area}>
              <div className="mb-1 flex items-baseline justify-between gap-2">
                <span className="truncate text-xs font-semibold text-slate-700">{a.area}</span>
                <span className="shrink-0 text-xs font-bold text-slate-900">{pct} %</span>
              </div>
              <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
                {a.covered > 0 && <div className="bg-emerald-500" style={{ width: `${(a.covered / a.total) * 100}%` }} />}
                {a.partial > 0 && <div className="bg-amber-400" style={{ width: `${(a.partial / a.total) * 100}%` }} />}
                {a.not_found > 0 && <div className="bg-rose-400" style={{ width: `${(a.not_found / a.total) * 100}%` }} />}
                {a.unverifiable > 0 && <div className="bg-slate-300" style={{ width: `${(a.unverifiable / a.total) * 100}%` }} />}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500">
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Doloženo</span>
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-amber-400" /> Částečně</span>
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-rose-400" /> Nenalezeno</span>
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-slate-300" /> {unverifiableLabel || "Neověřitelné"}</span>
      </div>
    </div>
  );
};
